(function () {
  class TimelineBackground {
    constructor(container) {
      this.container = container;
      this.progressLine = null;
      this.progressFill = null;
      this.items = [];
      this.observer = null;
      this.itemObservers = [];
      this.isInViewport = false;
      this.ticking = false;
      this.activeItems = new Set();
      this.handleScroll = this.onScroll.bind(this);
      this.handleResize = this.onResize.bind(this);
      this.init();
    }

    init() {
      this.setupElements();
      this.setupObserver();
      this.setupItemObservers();
      this.bindEvents();
      this.setupDeviceDetection();

      // Recalculate after images/fonts loaded
      window.addEventListener("load", () => {
        this.recalculate();
      });

      // Watch for text changes (translation apps like Weglot/Langify)
      this.contentObserver = new MutationObserver(() => {
        clearTimeout(this.contentChangeTimeout);
        this.contentChangeTimeout = setTimeout(() => {
          this.recalculate();
        }, 200);
      });
      this.contentObserver.observe(this.container, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    recalculate() {
      this.calculateProgressLineHeight();
      this.updateProgress();
    }

    setupElements() {
      this.progressLine = this.container.querySelector("[data-timeline-progress]");
      this.progressFill = this.container.querySelector(".timeline-background-1__progress-fill");
      this.items = Array.from(this.container.querySelectorAll(".timeline-background-1__item"));
      this.calculateProgressLineHeight();
    }

    calculateProgressLineHeight() {
      if (!this.progressLine || this.items.length < 2) return;

      const firstNumber = this.items[0].querySelector(".timeline-background-1__number");
      const lastNumber = this.items[this.items.length - 1].querySelector(".timeline-background-1__number");

      if (!firstNumber || !lastNumber) return;

      const containerRect = this.progressLine.parentElement.getBoundingClientRect();
      const firstRect = firstNumber.getBoundingClientRect();
      const lastRect = lastNumber.getBoundingClientRect();

      const firstCenter = firstRect.top + firstRect.height / 2 - containerRect.top;
      const lastCenter = lastRect.top + lastRect.height / 2 - containerRect.top;

      this.progressLine.style.top = firstCenter + "px";
      this.progressLine.style.height = (lastCenter - firstCenter) + "px";
    }

    setupObserver() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.isInViewport = entry.isIntersecting;
            if (this.isInViewport) {
              this.updateProgress();
            }
          });
        },
        {
          root: null,
          rootMargin: "50% 0px 50% 0px",
          threshold: 0,
        }
      );

      this.observer.observe(this.container);
    }

    // IntersectionObserver per item — reliable on iOS during momentum scroll
    setupItemObservers() {
      this.destroyItemObservers();

      this.items.forEach((item, index) => {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.activeItems.add(index);
              } else if (entry.boundingClientRect.top > window.innerHeight / 2) {
                // Only deactivate if item is below center (scrolling back up)
                this.activeItems.delete(index);
              }
              this.applyActiveStates();
              this.applyProgressFromActiveItems();
            });
          },
          {
            root: null,
            // Trigger when item crosses the center of the viewport
            rootMargin: "-50% 0px -50% 0px",
            threshold: 0,
          }
        );

        observer.observe(item);
        this.itemObservers.push(observer);
      });
    }

    applyActiveStates() {
      this.items.forEach((item, index) => {
        const number = item.querySelector(".timeline-background-1__number");
        const content = item.querySelector(".timeline-background-1__item-content");
        const isActive = this.activeItems.has(index);

        if (number) number.classList.toggle("active", isActive);
        if (content) content.classList.toggle("active", isActive);
      });
    }

    applyProgressFromActiveItems() {
      if (!this.progressFill || this.items.length === 0) return;

      if (this.activeItems.size === 0) {
        this.progressFill.style.height = "0%";
        return;
      }

      const maxIndex = Math.max(...this.activeItems);
      const progress = ((maxIndex + 1) / this.items.length) * 100;
      this.progressFill.style.height = progress + "%";
    }

    // Scroll-based update — smooth on desktop, complements IO on mobile
    updateProgress() {
      if (!this.progressFill || this.items.length === 0 || !this.isInViewport) return;

      const firstNumber = this.items[0].querySelector(".timeline-background-1__number");
      const lastNumber = this.items[this.items.length - 1].querySelector(".timeline-background-1__number");

      if (!firstNumber || !lastNumber) return;

      const viewportCenter = window.innerHeight / 2;
      const firstCenter = firstNumber.getBoundingClientRect().top + firstNumber.offsetHeight / 2;
      const lastCenter = lastNumber.getBoundingClientRect().top + lastNumber.offsetHeight / 2;

      let scrollProgress = 0;

      if (firstCenter <= viewportCenter && lastCenter >= viewportCenter) {
        scrollProgress = (viewportCenter - firstCenter) / (lastCenter - firstCenter);
      } else if (firstCenter > viewportCenter) {
        scrollProgress = 0;
      } else if (lastCenter < viewportCenter) {
        scrollProgress = 1;
      }

      scrollProgress = Math.max(0, Math.min(1, scrollProgress));

      this.progressFill.style.height = (scrollProgress * 100) + "%";

      // Sync active states from scroll position
      this.activeItems.clear();
      this.items.forEach((item, index) => {
        const number = item.querySelector(".timeline-background-1__number");
        if (!number) return;
        const numberCenter = number.getBoundingClientRect().top + number.offsetHeight / 2;
        const isActive = numberCenter <= viewportCenter;

        if (isActive) this.activeItems.add(index);
        if (number) number.classList.toggle("active", isActive);

        const content = item.querySelector(".timeline-background-1__item-content");
        if (content) content.classList.toggle("active", isActive);
      });
    }

    onScroll() {
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          this.updateProgress();
          this.ticking = false;
        });
        this.ticking = true;
      }
    }

    onResize() {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.recalculate();
      }, 150);
    }

    bindEvents() {
      window.addEventListener("scroll", this.handleScroll, { passive: true });
      window.addEventListener("resize", this.handleResize, { passive: true });

      if (typeof Shopify !== "undefined" && Shopify.designMode) {
        document.addEventListener("shopify:section:load", (event) => {
          if (event.target.contains(this.container)) {
            this.destroy();
            setTimeout(() => this.init(), 100);
          }
        });

        document.addEventListener("shopify:section:unload", (event) => {
          if (event.target.contains(this.container)) {
            this.destroy();
          }
        });

        document.addEventListener("shopify:section:reorder", () => {
          this.recalculate();
        });

        document.addEventListener("shopify:block:select", () => {
          this.recalculate();
        });
      }
    }

    setupDeviceDetection() {
      const ctaButton = this.container.querySelector(".timeline-background-1__cta");
      if (!ctaButton) return;

      const iosLink = ctaButton.getAttribute("data-ios-link");
      const androidLink = ctaButton.getAttribute("data-android-link");
      const defaultLink = ctaButton.getAttribute("data-default-link");

      if (!iosLink && !androidLink) return;

      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      let detectedLink = defaultLink;

      if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
        detectedLink = iosLink || defaultLink;
      } else if (/Macintosh|MacIntel|MacPPC|Mac68K/i.test(userAgent)) {
        detectedLink = iosLink || defaultLink;
      } else if (/android/i.test(userAgent)) {
        detectedLink = androidLink || defaultLink;
      }

      if (detectedLink && detectedLink !== defaultLink) {
        ctaButton.href = detectedLink;
      }
    }

    destroyItemObservers() {
      if (this.itemObservers.length > 0) {
        this.itemObservers.forEach((observer) => observer.disconnect());
        this.itemObservers = [];
      }
    }

    destroy() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      this.destroyItemObservers();
      window.removeEventListener("scroll", this.handleScroll);
      window.removeEventListener("resize", this.handleResize);
      clearTimeout(this.resizeTimeout);
      clearTimeout(this.contentChangeTimeout);
      if (this.contentObserver) {
        this.contentObserver.disconnect();
        this.contentObserver = null;
      }
    }
  }

  function initTimelineBackground() {
    const sections = document.querySelectorAll(".timeline-background-1");

    sections.forEach((section) => {
      if (!section.timelineBackgroundInstance) {
        section.timelineBackgroundInstance = new TimelineBackground(section);
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initTimelineBackground);
  } else {
    initTimelineBackground();
  }

  // Re-init when new sections appear (multiple instances on the same page)
  const mo = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === 1 && (node.classList.contains("timeline-background-1") || node.querySelector(".timeline-background-1"))) {
          initTimelineBackground();
          return;
        }
      }
    }
  });
  mo.observe(document.body || document.documentElement, { childList: true, subtree: true });
})();
